// WidgetDisplayEditor.js
// The ⚙ "Card display" modal behind every dashboard card.
//
// The whole of a card's setup is three questions: which entry of the function's
// result to show, how to draw it, and (for a stat tile) which column to
// aggregate. The list of entries is NOT authored here — it is the manifest the
// function's latest run already produced, so a user can only bind a card to
// something that genuinely exists.
//
// The editor knows nothing about view documents: it takes one widget and hands
// back one widget, which is why the live view and the view builder can both open
// it unchanged.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import useNow from '../../hooks/useNow';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FolderIcon from '@mui/icons-material/Folder';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import WidgetCard from './widgets/WidgetCard';
import { RunOriginIcon, RunOutcomeGlyph } from '../common/RunChrome';
import { deriveRunOutcome, formatRunTime, runOriginLabel } from '../../utils/runDisplay';
import {
  DEFAULT_STAT_CONFIG,
  KIND_ALLOWED_DISPLAYS,
  KIND_DEFAULT_DISPLAY,
  STAT_AGGREGATIONS,
  STAT_FORMATS,
  compositeEntries,
  entryLabel,
  isEntryTruncated,
  normalizeWidget,
  numericColumns,
  rowColumns,
  sameBinding,
} from '../../utils/widgetModel';
import { countNoun } from '../../utils/pluralise';

const KIND_ICONS = {
  data: TableChartIcon,
  chart: BarChartIcon,
  file: InsertDriveFileIcon,
  folder: FolderIcon,
};

// What each non-data entry kind means as a card. Only `data` has a genuine
// choice of display, so the others get a sentence instead of a control.
const DISPLAY_NOTES = {
  tabs: 'Renders the whole tabbed composite exactly as the Decode function page does — every table, chart and file the run produced.',
  chart: "A chart entry renders as a chart card. The function's saved chart settings (title, colours, axes) still apply — same identity, same overrides as the Decode UI tabs.",
  file: 'A file entry renders the artifact this run stored, inline, with a download button. Files are never turned into tiles.',
  shelf: 'A folder entry renders as a list of downloadable files. Clicking one opens it full size.',
};

const SECTION_HEADING_SX = {
  display: 'block',
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  color: 'text.secondary',
};

function SectionHeading({ children }) {
  return <Typography variant="caption" sx={SECTION_HEADING_SX}>{children}</Typography>;
}

// One selectable row in the "What to show" list. The composite row uses the same
// shape as an entry row so the choice reads as one list, not a list plus an
// exception.
function EntryRow({ icon, label, kind, meta, selected, onClick }) {
  const Icon = icon;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.25,
        py: 1,
        minWidth: 0,
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        backgroundColor: selected ? 'custom.highlight' : 'transparent',
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover': { borderColor: selected ? 'primary.main' : 'text.disabled' },
      }}
    >
      <Icon fontSize="small" sx={{ color: selected ? 'primary.main' : 'text.disabled' }} />
      <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>{label}</Typography>
      <Chip label={kind} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
      <Typography
        variant="caption"
        color="text.disabled"
        noWrap
        sx={{ ml: 'auto', pl: 1, whiteSpace: 'nowrap' }}
      >
        {meta}
      </Typography>
    </Box>
  );
}

// A stat tile aggregates one column, and that column must exist in the entry it
// is bound to — carrying a column name over from a different entry would compute
// nothing. The label defaults to the entry's own name.
const seedStatConfig = (config, entry) => {
  const current = { ...DEFAULT_STAT_CONFIG, ...(config || {}) };
  const columns = rowColumns(entry?.rows);
  const numeric = numericColumns(entry?.rows);
  const column = columns.includes(current.column)
    ? current.column
    : (numeric[0] || columns[0] || '');
  return {
    ...current,
    column,
    label: current.label || entry?.label || entryLabel(entry),
  };
};

function WidgetDisplayEditor({
  open,
  widgetId,
  widget,
  instanceState = null,
  companyName = null,
  onClose,
  onSave,
  localOnly = false,
}) {
  const [draft, setDraft] = useState(() => normalizeWidget(widget));
  // Relative run times ("just now", "Yest") follow the shared clock.
  useNow();

  // The parent re-creates the widget object on every render, so the reset effect
  // reads it through a ref: depending on the object identity would throw away
  // the user's edits mid-session.
  const widgetRef = useRef(widget);
  useEffect(() => { widgetRef.current = widget; }, [widget]);
  useEffect(() => {
    if (open) setDraft(normalizeWidget(widgetRef.current));
  }, [open]);

  const run = instanceState?.run || null;
  const outputs = instanceState?.outputs;
  const entries = useMemo(() => compositeEntries(outputs), [outputs]);
  const selectedEntry = useMemo(
    () => entries.find((candidate) => sameBinding(candidate.binding, draft.binding)) || null,
    [entries, draft.binding]
  );

  // `count` works on any column, so every column is offered — the numeric ones
  // simply come first because they are what a tile is almost always summing.
  const columnOptions = useMemo(() => {
    const all = rowColumns(selectedEntry?.rows);
    const numeric = numericColumns(selectedEntry?.rows);
    return [...numeric, ...all.filter((column) => !numeric.includes(column))];
  }, [selectedEntry]);

  const statConfig = { ...DEFAULT_STAT_CONFIG, ...(draft.config || {}) };
  const isHeader = draft.display === 'header';
  // A column aggregation with no column can never compute, so it is not saved.
  // The latest run may simply have produced no rows — column names come from
  // the rows, so there is then nothing to choose from until it runs with data.
  const statNeedsColumn = draft.display === 'stat'
    && selectedEntry?.kind === 'data'
    && statConfig.agg !== 'count'
    && !columnOptions.includes(statConfig.column);

  const selectAllTabs = () => {
    setDraft((previous) => ({ ...previous, binding: null, display: 'tabs' }));
  };

  const selectEntry = (entry) => {
    setDraft((previous) => {
      const allowed = KIND_ALLOWED_DISPLAYS[entry.kind] || [];
      const display = allowed.includes(previous.display)
        ? previous.display
        : KIND_DEFAULT_DISPLAY[entry.kind];
      return {
        ...previous,
        binding: entry.binding,
        display,
        config: display === 'stat' ? seedStatConfig(previous.config, entry) : previous.config,
      };
    });
  };

  const selectDisplay = (display) => {
    if (!display) return;
    setDraft((previous) => ({
      ...previous,
      display,
      config: display === 'stat' ? seedStatConfig(previous.config, selectedEntry) : previous.config,
    }));
  };

  const updateConfig = (patch) => {
    setDraft((previous) => ({ ...previous, config: { ...(previous.config || {}), ...patch } }));
  };

  const handleSave = () => {
    onSave(widgetId, normalizeWidget(draft));
    onClose();
  };

  // Data-backed cards need a completed run before their manifest entries are
  // known — the entries come from the outputs, which a re-run delivers before
  // its run document (shown as pending provenance meanwhile). Headers are
  // deliberately run-independent and take their content directly from the
  // widget configuration.
  const hasEntryChoice = Boolean(instanceState && (run || Object.keys(outputs || {}).length > 0));

  // A saved card can point at an entry this run did not produce — a conditional
  // artifact, or a republished function. Nothing in the list is selected then,
  // so the note has to explain the empty selection.
  const displayNote = (draft.binding && !selectedEntry)
    ? 'This card is bound to an entry the latest run did not produce. Choose another entry above.'
    : (DISPLAY_NOTES[draft.binding ? draft.display : 'tabs'] || '');

  // The View builder cannot list a run's entries until that run's complete
  // result has loaded (a run that stored no preview); it passes the reason so
  // this editor never mistakes "not loaded yet" for "has never run", and, when
  // the download failed, `onRetryEntries` to try it again from here.
  const noEntriesReason = instanceState?.entriesUnknownReason
    || "This card's function has no completed run yet, so its entries are not known. Run it once to choose an entry.";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* `component="div"` because the title row holds a Box and its own
          Typography, which are not valid inside the default heading element. */}
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {isHeader
            ? 'Edit header'
            : (instanceState?.functionName ? `Card display — ${instanceState.functionName}` : 'Card display')}
        </Typography>
        {hasEntryChoice && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mt: 0.5,
              color: 'text.disabled',
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="caption" color="text.disabled">
              Entries below were produced by the latest run
            </Typography>
            {run && (
              <>
                <RunOriginIcon triggerType={run.trigger_type} fontSize={12} />
                <Typography variant="caption" color="text.disabled">
                  {`${runOriginLabel(run.trigger_type)} · ${formatRunTime(run.created_at)}`}
                </Typography>
                <RunOutcomeGlyph outcome={deriveRunOutcome(run)} fontSize={12} />
                {(run.function_version || run.function_version === 0) && (
                  <Box
                    component="span"
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 5, px: 0.75, fontSize: 10, lineHeight: 1.6 }}
                  >
                    {`v${run.function_version}`}
                  </Box>
                )}
              </>
            )}
            {!run && <Typography variant="caption" color={instanceState?.runDocError ? 'warning.main' : 'text.disabled'}>
              {instanceState?.runDocError ? 'Run details unavailable' : 'Updating run details…'}
            </Typography>}
            {!run && instanceState?.runDocError && instanceState.onRetryRunDoc && (
              <Button size="small" onClick={instanceState.onRetryRunDoc}>Retry run details</Button>
            )}
          </Box>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', minHeight: 420 }}>
          <Box
            sx={{
              width: 380,
              flex: '0 0 380px',
              borderRight: 1,
              borderColor: 'divider',
              p: 2,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {isHeader ? (
              <>
                <SectionHeading>Header content</SectionHeading>
                <TextField
                  autoFocus
                  size="small"
                  fullWidth
                  label="Header"
                  value={typeof draft.config?.text === 'string' ? draft.config.text : ''}
                  onChange={(event) => updateConfig({ text: event.target.value })}
                  sx={{ mb: 0 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  label="Subtitle"
                  value={typeof draft.config?.subtitle === 'string' ? draft.config.subtitle : ''}
                  onChange={(event) => updateConfig({ subtitle: event.target.value })}
                  sx={{ mb: 0 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Headers are static View labels and do not depend on a Function run.
                </Typography>
              </>
            ) : hasEntryChoice ? (
              <>
                <SectionHeading>What to show</SectionHeading>
                <Stack spacing={0.75}>
                  <EntryRow
                    icon={ViewModuleIcon}
                    label="All tabs"
                    kind="composite"
                    meta="the whole function result"
                    selected={!draft.binding}
                    onClick={selectAllTabs}
                  />
                  {entries.map((entry) => (
                    <EntryRow
                      key={`${entry.binding.source_block_id}::${entry.binding.entry_key}`}
                      icon={KIND_ICONS[entry.kind] || InsertDriveFileIcon}
                      label={entry.label}
                      kind={entry.kind}
                      meta={entry.meta}
                      selected={sameBinding(entry.binding, draft.binding)}
                      onClick={() => selectEntry(entry)}
                    />
                  ))}
                </Stack>

                <SectionHeading>Display as</SectionHeading>
                {selectedEntry?.kind === 'data' ? (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    fullWidth
                    value={draft.display}
                    onChange={(_, value) => selectDisplay(value)}
                  >
                    <ToggleButton value="table">Table</ToggleButton>
                    <ToggleButton value="stat">Stat tile</ToggleButton>
                  </ToggleButtonGroup>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {displayNote}
                  </Typography>
                )}

                {selectedEntry?.kind === 'data' && draft.display === 'stat' && (
                  <>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="widget-stat-column">Column</InputLabel>
                      <Select
                        labelId="widget-stat-column"
                        label="Column"
                        value={columnOptions.includes(statConfig.column) ? statConfig.column : ''}
                        disabled={columnOptions.length === 0}
                        onChange={(event) => updateConfig({ column: event.target.value })}
                      >
                        {columnOptions.map((column) => (
                          <MenuItem key={column} value={column}>{column}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth>
                      <InputLabel id="widget-stat-agg">Aggregation</InputLabel>
                      <Select
                        labelId="widget-stat-agg"
                        label="Aggregation"
                        value={statConfig.agg || DEFAULT_STAT_CONFIG.agg}
                        onChange={(event) => updateConfig({ agg: event.target.value })}
                      >
                        {STAT_AGGREGATIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {statNeedsColumn && (
                      <Typography variant="caption" color="warning.main">
                        {columnOptions.length === 0
                          ? 'The latest result has no rows, so there are no columns to choose. Run it with data first, or use Count rows.'
                          : 'Choose a column to aggregate, or use Count rows.'}
                      </Typography>
                    )}

                    <TextField
                      size="small"
                      fullWidth
                      label="Label"
                      value={statConfig.label || ''}
                      onChange={(event) => updateConfig({ label: event.target.value })}
                      sx={{ mb: 0 }}
                    />

                    <FormControl size="small" fullWidth>
                      <InputLabel id="widget-stat-format">Format</InputLabel>
                      <Select
                        labelId="widget-stat-format"
                        label="Format"
                        value={statConfig.format || DEFAULT_STAT_CONFIG.format}
                        onChange={(event) => updateConfig({ format: event.target.value })}
                      >
                        {STAT_FORMATS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      The tile is one aggregation over one column of this entry&apos;s rows, like a
                      spreadsheet SUM. Anything conditional belongs in a Logic block.
                    </Typography>
                  </>
                )}
              </>
            ) : (
              <Alert
                severity="info"
                action={instanceState?.onRetryEntries ? (
                  <Button color="inherit" size="small" onClick={instanceState.onRetryEntries}>
                    Retry
                  </Button>
                ) : null}
              >
                {noEntriesReason}
              </Alert>
            )}

            <Box sx={{ mt: 'auto', pt: 1 }}>
              <TextField
                size="small"
                fullWidth
                label={isHeader ? 'Card title (optional)' : 'Card title'}
                placeholder={isHeader ? 'Optional label above the header' : 'Uses the entry name'}
                value={draft.title || ''}
                onChange={(event) => setDraft((previous) => ({ ...previous, title: event.target.value }))}
                sx={{ mb: 0 }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              backgroundColor: 'background.default',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <SectionHeading>Live preview</SectionHeading>
            <Box sx={{ width: '100%', maxWidth: 420, height: 260 }}>
              <WidgetCard
                widgetId={widgetId}
                widget={draft}
                instanceState={instanceState}
                companyName={companyName}
                onConfigure={null}
                draggable={false}
              />
            </Box>
            {/* A stat tile refuses to compute from a capped preview, so without
                this line its spinner would read as a broken card. */}
            {draft.display === 'stat' && isEntryTruncated(selectedEntry) && (
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420 }}>
                {`This run's preview holds ${selectedEntry.rows.length.toLocaleString()} of ${countNoun(selectedEntry.total_rows, 'row')}. The tile is computed from the full stored result, which loads once the card is saved.`}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        {localOnly && <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>Applies only to this open View.</Typography>}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={statNeedsColumn}>{localOnly ? 'Apply' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default WidgetDisplayEditor;
